<template>
  <div class="checkbox-wrapper">
    <div
      class="checkbox"
      :class="classes"
    >
      <div
        class="checkbox__clickable-area"
        @keydown.enter.prevent="handleChange"
        @click="handleLabelClick"
      >
        <label :aria-label="label">
          <input
            :id="name"
            ref="checkboxRef"
            v-bind="$attrs"
            class="checkbox__input has-outline"
            type="checkbox"
            tabindex="0"
            :aria-label="label"
            :disabled="$props.disabled"
            :name="name"
            :value="model"
            @change="handleChange"
            @blur="handleBlur"
            @focus="handleFocus"
          />

          <div class="checkbox__icon">
            <the-icon
              :art="iconData.art"
              :name="iconData.name"
            />
          </div>
        </label>

        <slot name="label">
          <span
            v-sanitize-html="label"
            class="checkbox__label"
            :class="$props.size === 'sm' ? 'body-2-expanded' : 'body-1'"
          />
        </slot>
      </div>
    </div>

    <div
      v-if="isInvalid"
      class="checkbox__error-text caption-regular"
    >
      <the-icon
        v-if="isInvalid"
        art="solid"
        class="checkbox__invalid-icon"
        name="circle-exclamation"
      />
      <span v-text="autoHint" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

import useI18n from '@/hooks/useI18n'

// HOOKS
const { t } = useI18n()

// INIT
const model = defineModel({ type: Boolean })
const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },

  label: {
    type: String,
    default: null,
  },

  name: {
    type: String,
    required: true,
  },

  size: {
    validator(value) {
      return ['sm', 'md'].includes(value)
    },
    default: 'sm',
  },

  v: {
    type: Object,
    default: null,
  },
})

// DATA
const checkboxRef = ref()

// COMPUTED
const classes = computed(() => {
  return {
    [`checkbox--${props.size}`]: true,
    'checkbox--selected': model.value,
    'checkbox--disabled': props.disabled,
    'checkbox--error': isInvalid.value,
  }
})

function handleBlur() {
  checkboxRef.value?.classList.remove('has-outline')
}

function handleFocus() {
  checkboxRef.value?.classList.add('has-outline')
}

const iconData = computed(() => {
  return {
    art: model.value === true ? 'solid' : 'regular',
    name: model.value === true ? 'square-check' : 'square',
  }
})

const isInvalid = computed(() => {
  return props.v?.$dirty && props.v?.$invalid
})

const autoHint = computed(() => {
  if (isInvalid.value) {
    const error = props.v?.$errors[0]?.$validator
    if (error) return t(`form.error.${error}`)
  }
  return ''
})

// METHODS
function handleLabelClick(event) {
  if (event.target.closest('a')) return
  handleChange()
}

function handleChange() {
  model.value = !model.value
  props.v?.$touch()
}
</script>

<style scoped>
.checkbox-wrapper {
  display: flex;
  flex-direction: column;
}

.checkbox {
  --checkbox-c-icon: var(--Interaction-States-enabled-off);
  --checkbox-c-icon-hovered: var(--Interaction-States-enabled-off);
  --checkbox-c-icon-active: var(--Interaction-States-enabled-off);
  --checkbox-c-icon-disabled: var(--Interaction-States-disabled-default);
  --checkbox-c-label: var(--on-surface);
  --checkbox-c-label-disabled: var(--Interaction-States-disabled-default-label);
  --checkbox-c-state-layer-hovered: var(--Interaction-States-hovered-default-b);
  --checkbox-c-state-layer-active: var(--Interaction-States-pressed-default-c);

  display: flex;
  align-items: center;
  gap: var(--fixed-spacing-fix-03);
  position: relative;

  &--selected {
    --checkbox-c-icon: var(--Interaction-States-enabled-default);
    --checkbox-c-icon-hovered: var(--Interaction-States-hovered-default-a);
    --checkbox-c-icon-active: var(--Interaction-States-pressed-default-a);
    --checkbox-c-state-layer-hovered: var(--Interaction-States-hovered-default-b);
    --checkbox-c-state-layer-active: var(--Interaction-States-pressed-default-c);
  }

  &--error {
    --checkbox-c-icon: var(--error);
    --checkbox-c-icon-hovered: var(--error);
    --checkbox-c-icon-active: var(--error);
    --checkbox-c-state-layer-hovered: var(--Interaction-States-hovered-default-error);
    --checkbox-c-state-layer-active: var(--Interaction-States-pressed-default-error);
  }

  &--sm {
    --checkbox-icon-size: 20px;
  }

  &--md {
    --checkbox-icon-size: 24px;
  }

  &__input {
    pointer-events: none;
    position: absolute;
    opacity: 0;
  }

  &__icon {
    position: relative;
    &::after {
      content: '';
      position: absolute;
      display: inline-block;
      height: 40px;
      width: 40px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: transparent;
      border-radius: 50%;
      z-index: -1;
    }
  }

  &__label {
    color: var(--checkbox-c-label);
  }

  &:has(.checkbox__input.has-outline:focus-visible) .checkbox__icon {
    outline: 2px solid var(--on-surface);
    outline-offset: var(--fixed-spacing-fix-01);
    border-radius: var(--fixed-border-radius-fix-02);
  }

  &:deep(.icon) {
    font-size: var(--checkbox-icon-size);
    color: var(--checkbox-c-icon);
    position: relative;
    z-index: var(--pet-stack-level-element);
  }

  &__clickable-area {
    display: flex;
    align-items: center;
    gap: var(--fixed-spacing-fix-03);
    cursor: pointer;
    position: relative;
  }

  &__clickable-area:hover {
    .checkbox__icon::after {
      background-color: var(--checkbox-c-state-layer-hovered);
    }

    &:deep(.icon) {
      color: var(--checkbox-c-icon-hovered);
    }
  }

  &__clickable-area:active {
    .checkbox__icon::after {
      background-color: var(--checkbox-c-state-layer-active);
    }

    &:deep(.icon) {
      color: var(--checkbox-c-icon-active);
    }
  }

  &--disabled {
    pointer-events: none;
    .checkbox__icon::after {
      background-color: transparent;
    }

    &:deep(.icon) {
      color: var(--checkbox-c-icon-disabled);
    }

    .checkbox__label {
      color: var(--checkbox-c-label-disabled);
    }
  }
}

.checkbox__error-text {
  color: var(--error);
  margin-top: var(--fixed-spacing-fix-03);
  height: var(--line-height-regular-xs);
  display: flex;
  align-items: center;
  gap: var(--fixed-spacing-fix-02);

  & span {
    display: inline-block;
    width: calc(100% - var(--fixed-spacing-fix-04));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.checkbox__invalid-icon {
  color: var(--error);
  font-size: var(--fixed-spacing-fix-04);
  flex-shrink: 0;
}
</style>
